import type { BridgeHealthResult } from '../../types/acombaBridge';

/** Raise together with the bridge's own `apiContract` when a route this app needs becomes required,
 * so an older bridge is named as out of date instead of failing one call at a time. */
export const MIN_BRIDGE_API_CONTRACT = 1;

/** What Acomba reports about the poste in the launch token. */
export type LaunchedBridgeStatus = 'absent' | 'stopped' | 'stale' | 'unhealthy' | 'healthy';

export type BridgeCause = 'connected' | 'absent' | 'stopped' | 'stale' | 'unhealthy' | 'unreachable';

export function isLaunchedBridgeStatus(value: unknown): value is LaunchedBridgeStatus {
    return value === 'absent' || value === 'stopped' || value === 'stale'
        || value === 'unhealthy' || value === 'healthy';
}

/**
 * Live `/health` wins whenever the bridge answers; the launch token only explains what the browser
 * cannot see, where never-installed, stopped and port-blocked all look identical.
 */
export function resolveBridgeCause(
    reachable: boolean,
    health: BridgeHealthResult | null,
    launched: LaunchedBridgeStatus | null,
): BridgeCause {
    if (reachable && health) {
        // `apiContract` is non-nullable on the health record, so its absence means the binary predates
        // the field — stale, not acceptable. Acomba concludes the same, reading it with a default of 0.
        if ((health.apiContract ?? 0) < MIN_BRIDGE_API_CONTRACT) return 'stale';
        // Up but unable to serve — no SDK registered, an SDK too old to trust, or an untrusted
        // certificate. A restart changes nothing.
        if (health.lastStartupError || !health.sdkVersion || health.sdkSupported === false) return 'unhealthy';
        return 'connected';
    }

    return launched && launched !== 'healthy' ? launched : 'unreachable';
}
